import React, { useMemo, useRef } from "react";
import Resizable from "react-resizable-layout";
import { Box } from "@mantine/core";
import { useAppSelector } from "../../app/hooks";
import { ValidationDiffTable } from "../../components/ValidationDiffTable";
import { selectHttpSingleRoomFull } from "../../Room/roomSelectors";
import { ValidationDecisionStatusPanel } from "../ValidationDecisionStatusPanel/ValidationDecisionStatusPanel";

const SPLIT_STORAGE_KEY = "standardRoomDiffSplitY";

type Props = {
  discipline: string;
};

export const StandardRoomDiff: React.FC<Props> = ({ discipline }) => {
  const room = useAppSelector(selectHttpSingleRoomFull);
  const disciplineValidation = room?.validation?.standardRoom?.[discipline];
  const containerRef = useRef<HTMLDivElement | null>(null);

  const initialPosition = useMemo(() => {
    if (typeof window === "undefined") return 260;
    const raw = window.localStorage.getItem(SPLIT_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n < 120) return 260;
    return n;
  }, []);

  if (!disciplineValidation) return <div>Ingen diff funnet for rommet</div>;

  const diffs = disciplineValidation.comparison ?? [];

  return (
    <Box ref={containerRef} style={{ height: "100%", minHeight: 0 }}>
      <Resizable
        axis="y"
        containerRef={containerRef}
        initial={initialPosition}
        min={120}
        onResizeEnd={(pos) => {
          if (typeof window !== "undefined") {
            window.localStorage.setItem(SPLIT_STORAGE_KEY, String(pos));
          }
        }}
      >
        {({ position, separatorProps }) => (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            {/* Top panel */}
            <div style={{ height: position, minHeight: 80, overflow: "hidden" }}>
              <ValidationDiffTable diffItems={diffs} />
            </div>

            {/* Separator */}
            <div {...separatorProps} style={{ flex: "0 0 auto" }}>
              <div
                style={{
                  height: 6,
                  cursor: "row-resize",
                  background: "linear-gradient(to bottom, #e0e0e0, #c8c8c8, #e0e0e0)",
                  borderRadius: 4,
                }}
              />
            </div>

            {/* Bottom panel - fills remaining height */}
            <div
              style={{
                flex: 1,
                minHeight: 0, // ✅ critical
                padding: 12,
                border: "1px solid #ddd",
                borderRadius: 8,
                background: "#fafafa",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Scroll container */}
              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <ValidationDecisionStatusPanel
                  validationDecisionKey={`standardRoom.${discipline}`}
                />
              </div>
            </div>
          </div>
        )}
      </Resizable>
    </Box>
  );
};
